import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Space,
  Spin,
  Table,
  notification,
  Tag,
  Typography,
  Progress,
  Dropdown,
  Modal,
  Button,
  Tooltip,
  Empty,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import moment from "moment";
import {
  FinetuneRunNew,
  FinetuneRunStatus,
} from "../../../model_gen/finetuning";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { useNavigate, Link } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import {
  PlusOutlined,
  MoreOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { PrimaryButton } from "../../shared/button/Buttons";
import { FinetuningService } from "../../../services/FinetuningService";
import { DatasetService } from "../../../services/DatasetService";

export const getTimeRemaining = (record: FinetuneRunNew) => {
  if (record.status === "RUNNING" && record.progress && record.date_created) {
    const startTime = moment(record.date_created);
    const now = moment();
    const elapsedMinutes = now.diff(startTime, "minutes");

    if (record.progress > 0 && elapsedMinutes > 0) {
      // Calculate total time based on progress so far
      const totalEstimatedMinutes = elapsedMinutes / record.progress;
      const remainingMinutes = totalEstimatedMinutes - elapsedMinutes;

      if (remainingMinutes <= 0) {
        return "Almost complete";
      }

      if (remainingMinutes < 60) {
        return `${Math.ceil(remainingMinutes)} minutes remaining`;
      } else {
        const hours = Math.floor(remainingMinutes / 60);
        const mins = Math.ceil(remainingMinutes % 60);
        return `${hours}h ${mins}m remaining`;
      }
    }
    return "Calculating...";
  }
  return null;
};

type FinetuneRunColumns = ColumnsType<FinetuneRunNew>;

const FinetuneRuns: React.FC = () => {
  const { apiKey } = usePermissions();
  const [finetuneRuns, setFinetuneRuns] = useState<FinetuneRunNew[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRuns, setTotalRuns] = useState(0);
  const pageSize = 10;
  const [datasets, setDatasets] = useState<{ [key: number]: any }>({});

  const navigate = useNavigate();

  const styles = {
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    detailsHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
  };

  // Add minimal CSS
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .finetune-runs-container {
        background-color: #f5f7fa;
      }
      .interactive-row {
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const getStatusTagColor = (status: FinetuneRunStatus): string => {
    switch (status.toUpperCase()) {
      case "PENDING":
        return "default";
      case "SCHEDULED":
        return "processing";
      case "RUNNING":
        return "processing";
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      case "CANCELLED":
        return "warning";
      default:
        return "default";
    }
  };

  const handleRetryRun = (run: FinetuneRunNew) => {
    FinetuneRunService.retryFinetuneRun(run.id, setIsLoading, apiKey).subscribe(
      () => {
        notification.success({
          message: "Success",
          description: "Finetune run retried successfully",
        });
        refreshFinetuneRuns(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to retry finetune run",
        });
      }
    );
  };

  const handleDeleteRun = (run: FinetuneRunNew) => {
    Modal.confirm({
      title: "Delete Finetune Run",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${run.display_name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        FinetuningService.updateFinetuneStatus(
          run.id.toString(),
          "DELETED",
          setIsLoading,
          apiKey
        ).subscribe(
          () => {
            notification.success({
              message: "Success",
              description: "Finetune run deleted successfully",
            });
            refreshFinetuneRuns();
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: "Failed to delete finetune run",
            });
          }
        );
      },
    });
  };

  // Fetch datasets when component mounts
  useEffect(() => {
    // Fetch all datasets - we'll use limit 1000 to ensure we get most datasets
    DatasetService.getDatasets(0, 1000, setIsLoading, apiKey).subscribe(
      (response: { items: any[]; total: number }) => {
        // Create a map of datasets by ID for quick lookup
        const datasetsMap = response.items.reduce((acc, dataset) => {
          acc[dataset.id] = dataset;
          return acc;
        }, {});
        setDatasets(datasetsMap);
      },
      (error: any) => {
        console.error("Failed to load datasets", error);
        notification.error({
          message: "Error",
          description: "Failed to load datasets information",
        });
      }
    );
  }, [apiKey]);

  const finetuneRunColumns: FinetuneRunColumns = [
    {
      title: "Name",
      key: "name",
      width: "25%",
      render: (_, record) => (
        <Typography.Text style={{ fontWeight: 500, color: "#1890ff" }}>
          {record.display_name}
        </Typography.Text>
      ),
      sorter: (a, b) => {
        const aName = a.display_name as string;
        const bName = b.display_name as string;
        return aName.localeCompare(bName);
      },
    },
    {
      title: "Base Model",
      key: "base_model",
      dataIndex: "base_model",
      width: "15%",
      render: (baseModel) => (
        <Tag
          color="purple"
          style={{
            padding: "4px 10px",
            borderRadius: "4px",
            fontSize: "12px",
          }}>
          {baseModel?.replace("Meta-", "")}
        </Tag>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "20%",
      render: (timestamp) => {
        if (!timestamp)
          return <Typography.Text type="secondary">-</Typography.Text>;
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) =>
        moment(a.date_created).unix() - moment(b.date_created).unix(),
    },
    {
      title: "Data Source",
      key: "data_source",
      width: "20%",
      render: (_, record) => {
        // Check if we have dataset information
        if (record.finetune_dataset_id) {
          const datasetId = record.finetune_dataset_id;
          const subDatasetId = record.meta?.sub_dataset_id;
          const dataset = datasets[datasetId];
          const subDataset = datasets[subDatasetId];

          if (dataset) {
            let displayName = dataset.name || "Dataset";

            // If there's a sub-dataset, add its information
            if (subDatasetId && subDataset?.name) {
              displayName += ` / ${subDataset.name}`;
            }
            const maxLength = 30;
            const truncatedName =
              displayName.length > maxLength
                ? `${displayName.substring(0, maxLength)}...`
                : displayName;

            return (
              <Tooltip title={displayName}>
                <Link
                  to={`/models/datasets/${subDatasetId || datasetId}`}
                  onClick={(e) => e.stopPropagation()}>
                  {truncatedName}
                </Link>
              </Tooltip>
            );
          } else {
            // We have the ID but not the dataset info yet
            return (
              <Link
                to={`/models/datasets/${subDatasetId || datasetId}`}
                onClick={(e) => e.stopPropagation()}>
                Dataset
              </Link>
            );
          }
        }

        // Fall back to previous behavior if dataset information is not available
        if (record.intent_group_name) {
          return <Typography.Text>{record.intent_group_name}</Typography.Text>;
        } else if (record.application_name && record.intent_name) {
          return (
            <Typography.Text>
              {record.application_name} / {record.intent_name}
            </Typography.Text>
          );
        } else {
          return <Typography.Text type="secondary">-</Typography.Text>;
        }
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15%",
      render: (status, record) => {
        const statusUpper = status.toUpperCase();

        // Calculate estimated time remaining for RUNNING status

        if (statusUpper === "RUNNING") {
          const percent = record.progress
            ? Math.round(record.progress * 100)
            : 0;
          return (
            <div style={{ width: "100px" }}>
              <Tooltip title={getTimeRemaining(record)}>
                <Progress
                  percent={Number(percent.toFixed(1))}
                  size="small"
                  status="active"
                />
              </Tooltip>
            </div>
          );
        } else if (statusUpper === "COMPLETED") {
          return (
            <div style={{ width: "100px" }}>
              <Progress percent={100} size="small" />
            </div>
          );
        } else {
          return <Tag color={getStatusTagColor(status)}>{statusUpper}</Tag>;
        }
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Accuracy",
      key: "accuracy",
      width: "15%",
      render: (_, record: FinetuneRunNew) => {
        const getScoreColor = (score: number) => {
          if (score >= 80) return "green"; // green
          if (score >= 60) return "yellow"; // yellow
          return "red"; // red
        };
        if (record.validation_accuracy === null) return "-";
        return (
          <Tag color={getScoreColor(record.validation_accuracy)}>
            {record.validation_accuracy.toFixed(2)}%
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: "5%",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleDeleteRun(record);
                },
              },
              {
                key: "retry",
                label: "Retry",
                disabled:
                  record.status.toUpperCase() != "FAILED" &&
                  record.status.toUpperCase() != "CANCELLED",
                icon: <ReloadOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleRetryRun(record);
                },
              },
            ],
          }}
          trigger={["click"]}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    // Initial data fetch - show loading indicator
    refreshFinetuneRuns(true);
  }, [apiKey, currentPage]); // Only trigger on apiKey or page changes

  // Create a function to check if there are running jobs
  const hasRunningJobs = () => {
    return finetuneRuns.some(
      (run) =>
        run.status.toUpperCase() === "RUNNING" ||
        run.status.toUpperCase() === "SCHEDULED"
    );
  };

  // Separate useEffect for the polling logic
  useEffect(() => {
    const pollingInterval = hasRunningJobs() ? 5000 : 60000; // 5s or 60s

    const intervalId = setInterval(() => {
      // Don't show loading indicator for background polling
      refreshFinetuneRuns(false);
    }, pollingInterval);

    // Clean up interval on component unmount or when dependencies change
    return () => clearInterval(intervalId);
  }, [apiKey, hasRunningJobs(), currentPage]); // Re-setup interval when running status changes

  const refreshFinetuneRuns = (showLoading = true) => {
    const offset = (currentPage - 1) * pageSize;
    FinetuneRunService.getFinetuneRuns(
      offset,
      pageSize,
      showLoading ? setIsLoading : null,
      apiKey
    ).subscribe(
      (response: { items: FinetuneRunNew[]; total: number }) => {
        setFinetuneRuns(response.items);
        setTotalRuns(response.total);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to load finetune runs",
        });
      }
    );
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  if (isLoading && finetuneRuns.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "1200px",
        margin: "0 auto",
        minHeight: "calc(100vh - 64px)",
      }}>
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <Row align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/">Home</Link> / Finetuning / Runs
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                Finetune Runs
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Manage and monitor your model fine-tuning runs
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <div style={styles.contentCard}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Finetune Runs
          </Typography.Title>
          <PrimaryButton
            title="New Finetune Run"
            icon={<PlusOutlined />}
            onClick={() => navigate("/models/finetuning/new")}
          />
        </div>
        <div style={styles.tableContainer}>
          <Table
            rowKey={(record) => record.id.toString()}
            columns={finetuneRunColumns}
            dataSource={finetuneRuns}
            loading={isLoading}
            rowClassName={() => "interactive-row"}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRuns,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} items`,
            }}
            onChange={handleTableChange}
            onRow={(record) => {
              return {
                onClick: () => navigate(`/models/finetuning/${record.id}`),
              };
            }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <div>
                      <Typography.Text
                        strong
                        style={{ display: "block", marginBottom: "8px" }}>
                        No finetune runs found
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        Create a new finetune run to start training your model.
                      </Typography.Text>
                    </div>
                  }
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FinetuneRuns;
